/**
 * Servicio de Gestión de Jornada de Trabajo y Despacho GPS.
 * 
 * Reglas de negocio:
 * 1. Estar autenticado NO activa el tracking por sí solo.
 * 2. Se activa con «Iniciar jornada» o al confirmar la primera visita del día.
 * 3. Indicador visible en la interfaz mientras esté activa la jornada.
 * 4. Límite máximo de 10 horas para prevenir seguimiento involuntario por olvido.
 * 5. Al finalizar jornada o cerrar sesión: detención total e inmediata de la captura.
 * 6. Encolado durable en SQLite y purga local solo ante confirmación HTTP 200 del servidor.
 */

import { enqueueGpsPoint, getPendingGpsPoints, confirmGpsPoints, markGpsPointsFailed, getPendingGpsCount } from "./gpsOfflineStorage.js";
import { readStoredAuth } from "../api/authApi.js";
import { requestWithFallback } from "../api/httpClient.js";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { getEndpointCandidates } from "../config/apiConfig.js";
import { resolveNativeTrackingUrls } from "./nativeTrackingEndpoints.js";
import { getTrackingPosition, describeTrackingLocationError } from "./trackingLocation.js";
import { guardarInicioJornada, guardarFinJornada, gpsFichado, leerJornadasLocales, sincronizarJornadas } from './jornadaOfflineService.js';
import { canRrhh } from './rrhhAccess.js';
import { ensureDataUsagePermission, syncDataUsageIfDue } from './dataUsageTelemetryService.js';

// Plugin nativo Capacitor para Foreground Service Android
const GpsTracking = registerPlugin("GpsTracking");

const STORAGE_KEY = "coordinacion_visitas_shift_state";
const DEFAULT_MAX_HOURS = 10; // Límite configurable de olvido: 10 horas

// Intervalos de muestreo espaciado
export const GPS_INTERVALS = {
    MOVING_FAST: 45 * 1000,      // 45 segundos durante traslado ("En camino")
    STATIONARY: 5 * 60 * 1000,   // 5 minutos cuando está detenido o en reposo
    MIN_DISTANCE_METERS: 15,     // Descartar desplazamientos menores a 15 metros en reposo
};

let shiftListeners = new Set();
let trackingIntervalTimer = null;
let lastRecordedPosition = null;
let startPromise = null;
let endPromise = null;
let trackingGeneration = 0;
let gpsHealth = { gpsStatus: 'unverified', gpsError: null, lastCapturedAt: null };

function updateGpsHealth(gpsStatus, gpsError = null) {
    gpsHealth = { ...gpsHealth, gpsStatus, gpsError };
    if (gpsStatus === 'ready') gpsHealth.lastCapturedAt = new Date().toISOString();
    notifyListeners();
}

function getStoredShiftState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            return JSON.parse(raw);
        }
    } catch {
        // Ignorar error de parsing
    }
    return {
        active: false,
        startedAt: null,
        maxDurationHours: DEFAULT_MAX_HOURS,
    };
}

function saveShiftState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Fallback silencioso
    }
    notifyListeners(state);
}

function notifyListeners(state = getStoredShiftState()) {
    for (const listener of shiftListeners) {
        try {
            listener({ ...state, ...gpsHealth });
        } catch (err) {
            console.warn("[shiftTrackingService] Error en listener:", err);
        }
    }
}

/**
 * Suscribe un callback a cambios de estado de la jornada.
 */
export function onShiftStatusChange(callback) {
    shiftListeners.add(callback);
    return () => shiftListeners.delete(callback);
}

/**
 * Obtiene el estado actual de la jornada laboral.
 */
export function getShiftStatus() {
    const state = getStoredShiftState();
    if (!state.active) {
        return {
            ...gpsHealth,
            active: false,
            startedAt: null,
            durationMinutes: 0,
            isExpired: false,
            maxDurationHours: state.maxDurationHours || DEFAULT_MAX_HOURS,
        };
    }

    const startedTime = new Date(state.startedAt).getTime();
    const elapsedMinutes = Math.max(0, Math.floor((Date.now() - startedTime) / (60 * 1000)));
    const maxMinutes = (state.maxDurationHours || DEFAULT_MAX_HOURS) * 60;
    const isExpired = elapsedMinutes >= maxMinutes;

    if (isExpired) {
        // Timeout de seguridad alcanzado: auto-finalizar
        void endShift("timeout").catch(() => {});
        return {
            ...gpsHealth,
            active: false,
            startedAt: null,
            durationMinutes: 0,
            isExpired: true,
            maxDurationHours: state.maxDurationHours || DEFAULT_MAX_HOURS,
        };
    }

    return {
        ...gpsHealth,
        active: true,
        userId: state.userId,
        startedAt: state.startedAt,
        jornadaId: state.jornadaId,
        schemaVersion: state.schemaVersion || 1,
        durationMinutes: elapsedMinutes,
        isExpired: false,
        maxDurationHours: state.maxDurationHours || DEFAULT_MAX_HOURS,
    };
}

/**
 * Valida si la jornada activa excedió el límite de seguridad.
 */
export function checkShiftTimeout() {
    return getShiftStatus();
}

/**
 * Inicia la jornada de trabajo.
 */
export function startShift({ visitaId = null, expectedUserId = null } = {}) {
    return activateTracking(true, visitaId, expectedUserId);
}

function isTodayOnDevice(value) {
    if (!value) return false;
    const date = new Date(value);
    const today = new Date();
    return date.getFullYear() === today.getFullYear()
        && date.getMonth() === today.getMonth()
        && date.getDate() === today.getDate();
}

/** Se invoca solamente después de confirmar el inicio de una visita. */
export async function ensureShiftForVisit(visitaId, { expectedUserId = null } = {}) {
    const generation = trackingGeneration;
    const session = await readStoredAuth();
    if (!session?.user?.id || generation !== trackingGeneration
        || (expectedUserId !== null && String(expectedUserId) !== String(session.user.id))) {
        throw new Error('La sesión cambió. Volvé a iniciar la jornada desde tu cuenta.');
    }

    if (endPromise) await endPromise;
    let current = getStoredShiftState();
    if (current.active && String(current.userId) !== String(session.user.id)) {
        await endShift('user_changed');
        current = getStoredShiftState();
    }

    const status = getShiftStatus();
    if (status.isExpired && endPromise) {
        await endPromise;
        return { ...getShiftStatus(), autoStarted: false, skipped: 'closed_today' };
    }
    current = getStoredShiftState();
    const sameUser = String(current.userId) === String(session.user.id);

    if (status.active && status.gpsStatus === 'ready') {
        return { ...status, autoStarted: false };
    }

    // No reabrir una jornada que ya se cerró, ni extender el límite de diez horas.
    if (!status.active && sameUser && isTodayOnDevice(current.endedAt)
        && ['manual', 'timeout'].includes(current.endReason)) {
        return { ...status, autoStarted: false, skipped: 'closed_today' };
    }

    if (!canRrhh(session.user, 'jornadas.iniciar')) return { ...getShiftStatus(), autoStarted: false, skipped: 'not_allowed' };
    const result = await startShift({ visitaId, expectedUserId: session.user.id });
    if (!result.active || result.gpsStatus !== 'ready') {
        throw new Error(result.gpsError || 'El inicio de jornada se canceló. Activá la ubicación desde la barra superior.');
    }
    return { ...result, autoStarted: !status.active };
}

/** Revalida una jornada existente al volver a la app, sin pedir permisos nativos. */
export async function resumeShiftTracking() {
    if (endPromise) { await endPromise; return getShiftStatus(); }
    if (!getShiftStatus().active) {
        try {
            const session = await readStoredAuth();
            if (!session?.user?.id) return getShiftStatus();
            const abierto = (await leerJornadasLocales(session.user.id)).find((row) => !row.fin);
            if (!abierto) return getShiftStatus();
            saveShiftState({ active: true, userId: session.user.id, startedAt: abierto.inicio.at, jornadaId: abierto.id, schemaVersion: abierto.schema_version || 1, maxDurationHours: DEFAULT_MAX_HOURS });
            if (!getShiftStatus().active) return getShiftStatus();
        } catch { return getShiftStatus(); }
    }
    const generation = trackingGeneration;
    try {
        const session = await readStoredAuth();
        const current = getStoredShiftState();
        if (generation !== trackingGeneration || !current.active) return getShiftStatus();
        if (!current.userId) {
            updateGpsHealth('error', 'La jornada guardada pertenece a una versión anterior. Tocá Revisar jornada para confirmar el registro en este dispositivo.');
            return getShiftStatus();
        }
        if (String(current.userId) !== String(session?.user?.id)) {
            await endShift('user_changed');
            return getShiftStatus();
        }
        return await activateTracking(false);
    } catch {
        return getShiftStatus(); // El motivo permanece visible en el control de jornada.
    }
}

function activateTracking(requestPermission, visitaId = null, expectedUserId = null) {
    if (startPromise) return startPromise;
    const generation = trackingGeneration;
    startPromise = (async () => {
        if (endPromise) {
            await endPromise;
            if (!requestPermission) return getShiftStatus();
        }
        let current = getStoredShiftState();
        let fichadoGuardado = false;
        updateGpsHealth('checking');
        try {
            const session = await readStoredAuth();
            if (!session?.user?.id) throw new Error('Iniciá sesión para activar tu jornada.');
            if (requestPermission && !canRrhh(session.user, 'jornadas.iniciar')) throw new Error('Tu cuenta no tiene permiso para iniciar la jornada.');
            if (expectedUserId !== null && String(expectedUserId) !== String(session.user.id)) {
                throw new Error('La sesión cambió. Volvé a iniciar la jornada desde tu cuenta.');
            }
            const locales = await leerJornadasLocales(session.user.id);
            const cacheCerrada = locales.find((row) => row.id === current.jornadaId && row.fin);
            if (cacheCerrada) {
                current = { ...current, active: false, startedAt: null, endedAt: cacheCerrada.fin.at, endReason: cacheCerrada.fin.motivo };
                saveShiftState(current);
                if (!requestPermission) return getShiftStatus();
            }
            const abiertos = locales.filter((row) => !row.fin);
            if (abiertos.length) {
                const abierto = abiertos[0];
                current = { ...current, active: true, userId: session.user.id, startedAt: abierto.inicio.at, jornadaId: abierto.id, schemaVersion: abierto.schema_version || 1 };
            }
            if (requestPermission && !current.active) await ensureDataUsagePermission(session.user);
            const position = await getTrackingPosition({ requestPermission });
            if (generation !== trackingGeneration) throw new Error('Inicio de jornada cancelado.');
            await startNativeForegroundService();
            if (generation !== trackingGeneration) {
                await stopNativeForegroundService();
                throw new Error('Inicio de jornada cancelado.');
            }
            const startedAt = current.active && String(current.userId) === String(session.user.id)
                ? current.startedAt : new Date().toISOString();
            const jornada = (current.active && current.jornadaId && locales.find((r) => r.id === current.jornadaId))
                || await guardarInicioJornada({ userId: session.user.id, startedAt, position, visitaId });
            const jornadaId = jornada.id;
            if (generation !== trackingGeneration) {
                await guardarFinJornada(jornadaId, session.user.id, { at: new Date().toISOString(), gps: null, motivo: 'logout', descanso_minutos: 0 });
                await stopNativeForegroundService();
                throw new Error('Inicio de jornada cancelado.');
            }
            saveShiftState({
                ...current,
                active: true,
                userId: session.user.id,
                startedAt,
                jornadaId,
                schemaVersion: jornada.schema_version || 1,
                maxDurationHours: current.maxDurationHours || DEFAULT_MAX_HOURS,
            });
            fichadoGuardado = true;
            if (requestPermission) void syncDataUsageIfDue({ trigger: 'shift_start', expectedUserId: session.user.id }).catch(() => {});
            lastRecordedPosition = null;
            void sincronizarJornadas().catch(() => {});
            await recordGpsPosition(position, visitaId);
            if (generation === trackingGeneration && getStoredShiftState().active) startTrackingInterval();
            return getShiftStatus();
        } catch (error) {
            if (generation === trackingGeneration) {
                stopTrackingInterval();
                await stopNativeForegroundService();
                if (!current.active && !fichadoGuardado) saveShiftState(current);
                const message = describeTrackingLocationError(error);
                if (error?.code === 'DATA_USAGE_PERMISSION_REQUIRED') { updateGpsHealth('unverified'); throw error; }
                updateGpsHealth('error', message);
                throw new Error(message);
            }
            throw error;
        }
    })().finally(() => { startPromise = null; });
    return startPromise;
}

/**
 * Finaliza la jornada de trabajo y detiene el tracking de forma total.
 */
export function endShift(reason = "manual", descansoMinutos = 0) {
    if (endPromise) return endPromise;
    endPromise = cerrarJornada(reason, descansoMinutos).finally(() => { endPromise = null; });
    return endPromise;
}

async function cerrarJornada(reason, descansoMinutos) {
    trackingGeneration += 1;
    stopTrackingInterval();
    const current = getStoredShiftState();
    gpsHealth = { gpsStatus: 'unverified', gpsError: null, lastCapturedAt: null };
    if (!current.active) {
        notifyListeners();
        return { active: false };
    }

    const endedAt = new Date().toISOString();
    await stopNativeForegroundService();
    let position = null;
    if (reason === 'manual') {
        try { position = await getTrackingPosition({ requestPermission: false }); } catch { /* El cierre se guarda aun sin GPS. */ }
    }
    const jornadaId = current.jornadaId || (await guardarInicioJornada({ userId: current.userId, startedAt: current.startedAt, position: null })).id;
    try {
        await guardarFinJornada(jornadaId, current.userId, { at: endedAt, gps: gpsFichado(position), motivo: reason === 'user_changed' ? 'user_changed' : reason,
            descanso_minutos: Math.max(0, Math.floor(Number(descansoMinutos) || 0)) });
    } catch (error) {
        updateGpsHealth('error', 'No se pudo guardar el cierre. Reintentá finalizar la jornada.');
        throw error;
    }
    const newState = {
        active: false,
        userId: current.userId,
        startedAt: null,
        endedAt,
        endReason: reason,
        maxDurationHours: current.maxDurationHours || DEFAULT_MAX_HOURS,
    };
    saveShiftState(newState);
    void syncDataUsageIfDue({ force: true, trigger: 'shift_end', expectedUserId: current.userId }).catch(() => {});
    void sincronizarJornadas().catch(() => {});

    // Conservar todo punto sin confirmación, incluso al finalizar sin conexión.
    try {
        await flushGpsQueueToServer();
    } catch {
        // La cola durable se reintentará cuando vuelva la conexión.
    }

    return newState;
}

/**
 * Detiene la jornada al cerrar sesión.
 */
export async function handleLogout() {
    await endShift("logout");
}

/**
 * Inicia el Foreground Service nativo de Android vía Capacitor.
 */
async function startNativeForegroundService() {
    if (Capacitor.getPlatform() !== 'android') {
        return;
    }

    try {
        const session = await readStoredAuth();
        const token = session?.token || "";
        const userId = Number(session?.user?.id) || 0;
        const empresaId = Number(session?.user?.empresa_id) || 0;

        console.log("[shiftTrackingService] Solicitando inicio de GpsTracking nativo con userId:", userId, "empresaId:", empresaId);

        const res = await GpsTracking.startTracking({
            title: "Jornada activa - Coordinación de Visitas",
            message: "Compartiendo ubicación en segundo plano",
            token,
            userId,
            empresaId,
            intervalMs: GPS_INTERVALS.MOVING_FAST,
            locationUrls: resolveNativeTrackingUrls(getEndpointCandidates('tracking', 'storeLocation', { isEndpointKey: true })),
        });
        console.log("[shiftTrackingService] Foreground Service nativo iniciado exitosamente:", JSON.stringify(res));
    } catch (err) {
        console.warn("[shiftTrackingService] Plugin GpsTracking fallo nativo:", err?.message || err);
        if (String(err?.code || '').startsWith('OS-PLUG-GLOC-')) throw err;
        throw new Error('No se pudo iniciar el servicio GPS. Reintentá con la app abierta; si persiste, verificá que tengas la app actualizada.');
    }
}

/**
 * Detiene el Foreground Service nativo de Android.
 */
async function stopNativeForegroundService() {
    if (Capacitor.getPlatform() !== 'android') {
        return;
    }

    try {
        await GpsTracking.stopTracking();
    } catch (err) {
        console.warn("[shiftTrackingService] Error al detener GpsTracking nativo:", err?.message || err);
    }
}

/**
 * Inicia el temporizador de muestreo en WebView / app activa.
 */
function startTrackingInterval() {
    stopTrackingInterval();
    // Ejecutar cada 60s en WebView para verificar sincronización y timeout
    trackingIntervalTimer = setInterval(async () => {
        const status = getShiftStatus();
        if (!status.active) {
            stopTrackingInterval();
            return;
        }

        await captureAndProcessCurrentLocation();
        await flushGpsQueueToServer();
    }, 60 * 1000);
}

function stopTrackingInterval() {
    if (trackingIntervalTimer) {
        clearInterval(trackingIntervalTimer);
        trackingIntervalTimer = null;
    }
}

/**
 * Captura la ubicación actual del dispositivo, evalúa desplazamiento y la encola.
 */
export async function captureAndProcessCurrentLocation(visitaId = null) {
    const status = getShiftStatus();
    if (!status.active) {
        return null;
    }

    const generation = trackingGeneration;
    try {
        const position = await getTrackingPosition();
        if (generation !== trackingGeneration || !getStoredShiftState().active) return null;
        return await recordGpsPosition(position, visitaId);
    } catch (err) {
        if (generation === trackingGeneration && getStoredShiftState().active) {
            updateGpsHealth('error', describeTrackingLocationError(err));
        }
        console.warn("[shiftTrackingService] Error al capturar GPS:", err?.message || err);
        return null;
    }
}

async function recordGpsPosition(position, visitaId = null) {
    const generation = trackingGeneration;
    const { latitude, longitude, accuracy } = position.coords;
    updateGpsHealth('ready');

    // Descarte de ruido en reposo: si el desplazamiento es menor a 15 metros, no saturar
    if (lastRecordedPosition && !visitaId) {
        const dist = calculateHaversineDistance(
            lastRecordedPosition.latitude,
            lastRecordedPosition.longitude,
            latitude,
            longitude
        );
        if (dist < GPS_INTERVALS.MIN_DISTANCE_METERS) {
            // Posición casi idéntica en reposo; se omite para optimizar recursos
            return null;
        }
    }

    const session = await readStoredAuth();
    const userId = session?.user?.id || null;

    let batteryLevel = null;
    if (typeof navigator !== "undefined" && typeof navigator.getBattery === "function") {
        try {
            const batt = await navigator.getBattery();
            batteryLevel = Math.round(batt.level * 100);
        } catch {
            // Batería no disponible
        }
    }

    if (generation !== trackingGeneration || !getStoredShiftState().active) return null;
    const pointRecord = await enqueueGpsPoint({
        latitude,
        longitude,
        accuracy,
        batteryLevel,
        userId,
        visitaId,
        timestamp: new Date(position.timestamp || Date.now()).toISOString(),
    });
    lastRecordedPosition = { latitude, longitude };

    // Intentar envío inmediato si hay conexión
    await flushGpsQueueToServer();

    return pointRecord;
}

/**
 * Envía por lotes los puntos pendientes en SQLite hacia el backend.
 * Purga localmente SOLO los puntos confirmados por el servidor (HTTP 200).
 */
export async function flushGpsQueueToServer() {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
        return { sent: 0, pending: await getPendingGpsCount() };
    }

    const pendingPoints = await getPendingGpsPoints(30);
    if (!pendingPoints || pendingPoints.length === 0) {
        return { sent: 0, pending: 0 };
    }

    const session = await readStoredAuth();
    const token = session?.token;
    if (!token) {
        return { sent: 0, error: "No autenticado" };
    }

    // Preparar payload para envío por lote
    const payload = {
        points: pendingPoints.map((p) => ({
            uuid: p.uuid || p.id,
            latitude: p.latitude,
            longitude: p.longitude,
            accuracy: p.accuracy,
            battery_level: p.batteryLevel != null ? p.batteryLevel : p.battery_level,
            visita_id: p.visitaId != null ? p.visitaId : p.visita_id,
            timestamp: p.timestamp,
        })),
    };

    let responseData = null;
    try {
        const response = await requestWithFallback({
            moduleName: "tracking",
            endpoint: "batchLocation",
            endpointIsKey: true,
            method: "post",
            data: payload,
            token,
            timeout: 10000,
        });
        responseData = response?.data;
    } catch (err) {
        console.warn("[shiftTrackingService] Fallo al enviar lote GPS:", err?.message || err);
    }

    if (responseData && responseData.ok && Array.isArray(responseData.confirmed)) {
        // Eliminar de SQLite EXCLUSIVAMENTE los puntos confirmados por el servidor
        const deleted = await confirmGpsPoints(responseData.confirmed);
        if (Array.isArray(responseData.failed) && responseData.failed.length > 0) {
            await markGpsPointsFailed(responseData.failed);
        }
        return {
            sent: deleted,
            confirmed: responseData.confirmed.length,
            failed: (responseData.failed || []).length,
            pending: await getPendingGpsCount(),
        };
    } else {
        // Error de red o servidor: marcar reintentos sin eliminar de SQLite
        await markGpsPointsFailed(pendingPoints);
        return { sent: 0, error: "Fallo de conexión", pending: await getPendingGpsCount() };
    }
}

/**
 * Fórmula de Haversine para calcular distancia en metros entre dos coordenadas.
 */
function calculateHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Radio de la Tierra en metros
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
        Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}
